import type { HttpClient } from "./http-common"
import type { VenuesByDisciplineResponse } from "@/models/channel.model"
import type { TLocation, TLocationBody } from "@/models/location.model"
import { tryCatch } from "@/lib/try-catch"
import { LocationMapper } from "@/mappers/location.mapper"
import ApiServiceFactory from "./api-service-factory"

export class LocationsService {
  private httpClient: HttpClient

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async getAllLocations() {
    const { data, error } = await tryCatch(this.httpClient.get<TLocation[]>("/locations"))

    if (error) {
      console.error("Error fetching locations:", error)
      throw error
    }

    return LocationMapper.toDto(data)
  }

  async getLocationByCode(code: string) {
    const { data, error } = await tryCatch(this.httpClient.get<TLocation>(`/locations/${code}`))

    if (error) {
      console.error(`Error fetching location with code ${code}:`, error)
      throw error
    }

    return LocationMapper.toDto(data)
  }

  async getLocationsByVenue(venueCode: string) {
    const { data, error } = await tryCatch(this.httpClient.get<TLocation[]>(`/locations/venue/${venueCode}`))

    if (error) {
      console.error(`Error fetching locations for venue ${venueCode}:`, error)
      throw error
    }

    return LocationMapper.toDto(data)
  }

  async getLocationsByDiscipline(disciplineCode: string) {
    const { data, error } = await tryCatch(this.httpClient.get<TLocation[]>(`/locations/discipline/${disciplineCode}`))

    if (error) {
      console.error(`Error fetching locations for discipline ${disciplineCode}:`, error)
      throw error
    }

    return LocationMapper.toDto(data)
  }

  async getVenuesByDiscipline(disciplineCode: string) {
    const { data, error } = await tryCatch(this.httpClient.get<VenuesByDisciplineResponse>(`/locations/venues-by-discipline/${disciplineCode}`))

    if (error) {
      console.error(`Error fetching venues by discipline ${disciplineCode}:`, error)
      throw error
    }

    return data
  }

  async saveLocation(location: TLocationBody) {
    const { data, error } = await tryCatch(this.httpClient.post<TLocationBody>("/locations", location))

    if (error) {
      console.error("Error saving location:", error)
      throw error
    }

    return data
  }

  async updateLocation(location: TLocationBody & { id?: string }, id: string) {
    const { data, error } = await tryCatch(this.httpClient.put<TLocationBody>(`/locations/update/${id}`, location))

    if (error) {
      console.error(`Error updating location ${location.code}:`, error)
      throw error
    }

    return data
  }

  async deleteLocation(id: string) {
    const { data, error } = await tryCatch(this.httpClient.delete(`/locations/delete/${id}`))

    if (error) {
      console.error(`Error deleting location with id ${id}:`, error)
      throw error
    }

    return data
  }
}

export const locationsService = ApiServiceFactory.createService(LocationsService, "/")
